import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, List } from 'lucide-react';
import toast from 'react-hot-toast';

// Single-song playback model:
// MusicKit only ever holds the one song currently playing. When it ends
// (or the host skips), we look at the live vote-sorted queue and start
// whatever is on top. Votes and new songs take effect naturally at every
// track boundary — there is no MusicKit queue to keep in sync.
//
// The parent is told about every track change via onTrackChange(playlistKey),
// which reports it to the server so guests see now-playing and finished
// songs get marked as played.
const AppleMusicPlayer = ({ musicInstance, upNext = [], nowPlaying = null, onTrackChange, skipSignal = 0 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);

  // The playlist key of the song we started in MusicKit (null = idle)
  const currentKeyRef = useRef(null);
  // True after the host has pressed play once (autoplay policy + intent)
  const hasStartedRef = useRef(false);
  // Concurrency guard for load/advance operations
  const busyRef = useRef(false);
  // Latest queue, readable from stable event callbacks
  const upNextRef = useRef([]);
  const onTrackChangeRef = useRef(onTrackChange);

  const getMusicId = useCallback((song) => {
    const id = song.appleMusicId || song.originalId || song.id;
    return (id && typeof id === 'string' && id.trim() !== '' && id !== 'undefined') ? id : null;
  }, []);

  const appleUpNext = upNext.filter(
    (song) => song.musicService === 'apple' && getMusicId(song)
  );
  upNextRef.current = appleUpNext;
  onTrackChangeRef.current = onTrackChange;

  // Load one song into MusicKit and start it
  const playSong = useCallback(async (song, musicIdOverride = null) => {
    if (!musicInstance || busyRef.current) return;

    const musicId = musicIdOverride || getMusicId(song);
    if (!musicId) return;

    busyRef.current = true;
    setIsLoadingTrack(true);
    try {
      await musicInstance.setQueue({ songs: [musicId] });
      currentKeyRef.current = song.firebaseKey || song.songId || null;
      hasStartedRef.current = true;

      // Report the track change up (server marks the previous song played)
      if (onTrackChangeRef.current && currentKeyRef.current) {
        onTrackChangeRef.current(currentKeyRef.current);
      }

      // Give MusicKit a moment to settle the queue before playing
      await new Promise((resolve) => setTimeout(resolve, 300));
      await musicInstance.play();
    } catch (err) {
      console.warn('[AppleMusicPlayer] Failed to start song:', err.message);
      // MusicKit sometimes aborts the first play() right after setQueue
      if (err.message?.includes('abort') || err.name === 'AbortError') {
        try {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await musicInstance.play();
        } catch (retryErr) {
          console.warn('[AppleMusicPlayer] Retry also failed:', retryErr.message);
          toast.error(`Could not play "${song.name || 'song'}"`);
        }
      } else {
        toast.error(`Could not play "${song.name || 'song'}"`);
      }
    } finally {
      busyRef.current = false;
      setIsLoadingTrack(false);
    }
  }, [musicInstance, getMusicId]);

  // Move on to whatever the live queue says is next
  const advance = useCallback(async () => {
    const next = upNextRef.current[0];
    if (next) {
      await playSong(next);
    } else {
      // Queue is empty — go idle and tell the server nothing is playing
      currentKeyRef.current = null;
      if (onTrackChangeRef.current) {
        onTrackChangeRef.current(null);
      }
    }
  }, [playSong]);

  // Detect track end and advance. With a one-song queue, MusicKit reports
  // ended/completed (or stopped, in some versions) when the song finishes.
  const handlePlaybackStateChange = useCallback(() => {
    if (!musicInstance || busyRef.current || !currentKeyRef.current) return;

    const state = musicInstance.playbackState;
    const states = window.MusicKit?.PlaybackStates || {};
    const endedStates = [states.ended ?? 5, states.completed ?? 10, states.stopped ?? 4];

    if (endedStates.includes(state)) {
      // Guard against manual stop mid-song: only advance when the track
      // actually ran out (or MusicKit already dropped the item)
      const remaining = musicInstance.currentPlaybackTimeRemaining;
      if (!musicInstance.nowPlayingItem || remaining == null || remaining <= 1) {
        advance();
      }
    }
  }, [musicInstance, advance]);

  // Playback state/progress UI updates
  const updatePlaybackState = useCallback(() => {
    if (!musicInstance) return;

    const state = musicInstance.playbackState;
    setIsPlaying(musicInstance.isPlaying || false);
    setCurrentTime(musicInstance.currentPlaybackTime || 0);
    setDuration(musicInstance.currentPlaybackDuration || 0);
    setIsBuffering(state === 1 || state === 8 || state === 9); // loading / waiting / stalled
  }, [musicInstance]);

  useEffect(() => {
    if (!musicInstance) return;

    musicInstance.addEventListener('playbackStateDidChange', updatePlaybackState);
    musicInstance.addEventListener('playbackStateDidChange', handlePlaybackStateChange);
    musicInstance.addEventListener('playbackTimeDidChange', updatePlaybackState);

    const intervalId = setInterval(updatePlaybackState, 250);
    updatePlaybackState();

    return () => {
      musicInstance.removeEventListener('playbackStateDidChange', updatePlaybackState);
      musicInstance.removeEventListener('playbackStateDidChange', handlePlaybackStateChange);
      musicInstance.removeEventListener('playbackTimeDidChange', updatePlaybackState);
      clearInterval(intervalId);
    };
  }, [musicInstance, updatePlaybackState, handlePlaybackStateChange]);

  // The crowd voted to skip the current song — move on
  useEffect(() => {
    if (skipSignal > 0 && currentKeyRef.current && !busyRef.current) {
      advance();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipSignal]);

  // If we're idle but songs are waiting (a new suggestion was approved while
  // the queue was dry), start the top one — only after the host has pressed
  // play at least once, so playback never starts unprompted.
  useEffect(() => {
    if (!musicInstance || !hasStartedRef.current) return;
    if (currentKeyRef.current || busyRef.current) return;
    if (appleUpNext.length === 0) return;

    const timeoutId = setTimeout(() => {
      if (!currentKeyRef.current && !busyRef.current && upNextRef.current.length > 0) {
        playSong(upNextRef.current[0]);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [appleUpNext.length, musicInstance, playSong]);

  const togglePlayPause = async () => {
    if (!musicInstance) {
      toast.error('Apple Music is not properly connected');
      return;
    }
    if (isLoadingTrack) return;

    try {
      if (isPlaying) {
        await musicInstance.pause();
        setIsPlaying(false);
        return;
      }

      if (currentKeyRef.current && musicInstance.nowPlayingItem) {
        // Resume the loaded track
        await musicInstance.play();
        setIsPlaying(true);
      } else if (nowPlaying?.appleMusicId) {
        // Page was refreshed mid-session: resume the song the server
        // says is playing rather than skipping it
        hasStartedRef.current = true;
        await playSong({ ...nowPlaying, firebaseKey: nowPlaying.songId }, nowPlaying.appleMusicId);
      } else {
        // Nothing loaded — start the top of the queue
        hasStartedRef.current = true;
        await advance();
      }
      setTimeout(updatePlaybackState, 100);
    } catch (error) {
      console.error('[AppleMusicPlayer] Error toggling playback:', error);
      toast.error(`Playback error: ${error.message}`);
    }
  };

  const skipToNext = async () => {
    if (!musicInstance || isLoadingTrack) return;
    await advance();
  };

  const restartCurrent = async () => {
    if (!musicInstance) return;
    try {
      await musicInstance.seekToTime(0);
    } catch (error) {
      console.error('[AppleMusicPlayer] Error restarting track:', error);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (musicInstance) {
      musicInstance.volume = newVolume;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!musicInstance) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Apple Music Player</h3>
        <p className="text-gray-400 text-center py-8">
          Connect to Apple Music to start playing songs
        </p>
      </div>
    );
  }

  const upNextSong = appleUpNext[0] || null;
  const hasAnything = nowPlaying || appleUpNext.length > 0;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-600">
      <h3 className="text-xl font-semibold text-white mb-4">Now Playing</h3>

      {hasAnything ? (
        <div className="space-y-4">
          {/* Song Info — from the server's shared now-playing state */}
          {nowPlaying && (
            <div className="flex items-center space-x-4">
              {nowPlaying.image && (
                <img
                  src={nowPlaying.image}
                  alt={nowPlaying.name}
                  className="w-16 h-16 rounded-lg"
                />
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white truncate">{nowPlaying.name}</h4>
                <p className="text-gray-400 truncate">{nowPlaying.artist}</p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="bg-gray-600 rounded-full h-3 overflow-hidden relative">
              <div
                className="bg-purple-500 h-full transition-all duration-100"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
              {isBuffering && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div className="flex justify-between text-sm text-gray-400 font-mono">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={restartCurrent}
              className="text-gray-400 hover:text-white active:text-purple-400 transition-colors p-2 -m-2"
              aria-label="Restart track"
            >
              <SkipBack className="w-6 h-6" />
            </button>

            <button
              onClick={togglePlayPause}
              disabled={isLoadingTrack}
              className={`${
                isLoadingTrack
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 active:bg-purple-800'
              } text-white rounded-full p-4 transition-all transform ${!isLoadingTrack ? 'active:scale-95' : ''}`}
              aria-label={isLoadingTrack ? 'Loading...' : (isPlaying ? 'Pause' : 'Play')}
            >
              {isLoadingTrack ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6 ml-0.5" />
              )}
            </button>

            <button
              onClick={skipToNext}
              disabled={isLoadingTrack || appleUpNext.length === 0}
              className="text-gray-400 hover:text-white active:text-purple-400 transition-colors p-2 -m-2 disabled:opacity-40"
              aria-label="Next track"
            >
              <SkipForward className="w-6 h-6" />
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center space-x-3">
            <Volume2 className="w-4 h-4 text-gray-400" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="flex-1 accent-purple-600"
            />
          </div>

          {/* Up Next */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-600">
            <div className="flex items-center space-x-2 text-sm text-gray-400 min-w-0">
              <List className="w-4 h-4 flex-shrink-0" />
              {upNextSong ? (
                <span className="truncate min-w-0">
                  Up next: <span className="text-gray-300">{upNextSong.name}</span> · {appleUpNext.length} in queue
                </span>
              ) : (
                <span>Queue is empty</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-400 text-center py-8">
          No songs in playlist. Approve some suggestions to start playing!
        </p>
      )}
    </div>
  );
};

export default AppleMusicPlayer;
